// This is the producer-owned source for contract/client-telemetry.json.
//
// Keep emitted event/error/route tokens here, then derive emitter types from this value. The
// generator reads this exact literal; a second hand-maintained list would recreate the cross-repo
// drift this contract exists to prevent.
export const CLIENT_TELEMETRY_CONTRACT = {
    event_types: {
        voice_playback_failed: [
            'aborted', // load/play cancelled (unmount or a new source)
            'decode', // bytes arrived but the backend could not decode them
            'http', // byte fetch answered with a non-OK status
            'network', // transport failure reaching the media
            'no-source', // neither url nor resolveUrl yielded a clip
            'not-allowed', // autoplay/gesture policy
            'not-supported', // codec or container unsupported by the backend
            'presign', // resolveUrl could not mint a clip URL
            'security', // cross-origin/CORS refusal
            'unknown',
        ],
        // Producer: `RenderFailureReporter` (the render boundary) + the
        // router-state tap. Names are the native ECMAScript error BRANDS — the
        // value's direct prototype must be the native's own `prototype` (neither
        // the mutable `error.name` nor the writable `constructor` field nor
        // base-class membership is consulted); everything else maps to `unknown`.
        // Set mirrors gateway-service
        // `clientTelemetryEventTypeAllowlist["render_failure"]` (landed
        // `gateway-service!301`); the cross-repo subset guard proves this list
        // never grows past it.
        render_failure: [
            'Error',
            'AggregateError',
            'EvalError',
            'RangeError',
            'ReferenceError',
            'SuppressedError',
            'SyntaxError',
            'TypeError',
            'URIError',
            'unknown',
        ],
        // Producer: `UnhandledExceptionReporter` (global `window.onerror` +
        // `unhandledrejection` listeners). RATIFIED against `render_failure`'s shipped
        // implementation (architect, `decisions/architect/2026-09-01-client-error-
        // event-type-set-contract-doc-ruling.md`, published 2026-09-13): "the grammar
        // is structural, so there are no per-type architectural questions left to
        // answer — only per-type table entries." Same closed set as `render_failure`
        // (identical native ECMAScript brands, same classification primitive —
        // `classifyNativeError` in `nativeErrorBrand.ts`, shared rather than
        // reimplemented) because both producers classify the SAME kind of value —
        // a native-or-unknown thrown/rejected error — through the SAME rule. The
        // per-type BUDGET (`CLIENT_EVENT_BUDGET.unhandled_exception`, `clientEvent.ts`)
        // has been live server-side since `web!513`, ahead of this declaration —
        // this contract entry is the one remaining precondition the ruling names.
        unhandled_exception: [
            'Error',
            'AggregateError',
            'EvalError',
            'RangeError',
            'ReferenceError',
            'SuppressedError',
            'SyntaxError',
            'TypeError',
            'URIError',
            'unknown',
        ],
        // Q4 (architect ruling, decisions/architect/2026-09-11-client-telemetry-admits-ability-row-
        // disagreement.md): reports a served AbilityRow whose flags contradict each other — a
        // gateway composer defect, never a client bug. `enabled_while_locked` is a row with
        // `enabled && !unlocked` (vision); `coupled_enabled_mismatch` is two rows listed in each
        // other's `coupled_ability_ids` arriving with different `enabled` (the scheduling pair).
        // No `unknown` catch-all — the ruling is explicit that a third disagreement shape needs its
        // own category decision, not a bucket. Carries `context.ability_id` (see below).
        ability_row_disagreement: ['enabled_while_locked', 'coupled_enabled_mismatch'],
        // Producer: the Umami send-window accounting on the `/api/send` senders
        // (`routeViewSender.ts`). Admitted by architect ruling 2026-09-18
        // (`decisions/architect/2026-09-18-client-telemetry-one-new-eventtype-in-the-
        // unconditional-window-shape.md`); the server side is `gateway-service!389`,
        // which admits EXACTLY these four spellings and nothing else.
        //
        // ⛔ A WINDOWED ACCOUNTING ROW, NOT A FAILURE EVENT, and that is the whole point:
        // an emitter that fires only on failure cannot distinguish "healthy" from
        // "detector dead" — BOTH produce no event. So this is emitted whenever a window
        // closes with `sent > 0`, and the event's ARRIVAL is itself the liveness signal.
        // Product's alarm is then expressible: `sent > 0 && accepted == 0 &&
        // unobservable == 0`.
        //
        //   http-4xx / http-5xx  the collector answered with that status class
        //   network              `fetch` REJECTED — transport failure OR an UNREADABLE error
        //                        response. ⛔ Do NOT narrow this to a transport-only meaning.
        //                        Those two causes are ONE observation from the browser: a 4xx
        //                        whose response lacks `access-control-allow-origin` arrives as
        //                        `TypeError: Failed to fetch`, indistinguishable from a dead
        //                        network (measured 2026-09-18, non-mutating probe). Defining it
        //                        by what the CLIENT could determine is what makes losing ACAO
        //                        move 4xx counts INTO `network` instead of blinding the
        //                        instrument.
        //   unknown              anything else
        //   no-failure           ⛔ NOT A FAILURE, and NOT "everything was accepted" — the name is
        //                        exact and the tempting alternative was WRONG. This token means only
        //                        that the window observed no classified failure. It CANNOT mean
        //                        `accepted == sent`, because `unobservable` is a residual: a window
        //                        can legitimately read `sent=5 accepted=3 rejected=0 unobservable=2`,
        //                        and a token claiming "all accepted" would be refuted by the event's
        //                        own counts — it would name the reassuring reading, which is the
        //                        exact defect the refusal of `unobservable` was about.
        //                        (This was my proposed spelling `all-accepted`; architect refused it
        //                        on that reasoning, 2026-09-18, and the refusal is correct.)
        //                        Why a token at all: `errorName` is REQUIRED on this wire
        //                        (`ClientEventInput`), and a missing one is not neutral — the
        //                        gateway's grammar check turns it into `__malformed__`, a FALSE
        //                        DEFECT REPORT about a well-formed event (`clienttelemetry.go`).
        //                        Every other token means "failed, for reason X", so emitting a
        //                        failure-free window as `unknown` would put two meanings under one
        //                        name. `ok` was refused too: §3's tokens name what the CLIENT could
        //                        DETERMINE, and `ok` is a verdict, not an observation.
        //                        ⚠️ The CI subset guard CANNOT catch this class: `CheckSubset`
        //                        validates the artifact's declared sets, not runtime behaviour.
        //
        // ⛔ PRECEDENCE WHEN ONE WINDOW HOLDS SEVERAL FAILURE CLASSES (architect §6, 2026-09-18).
        // A window can hold a 5xx AND a network failure, so the single `errorName` needs a rule —
        // descending collector-side blame, which is NOT descending severity:
        //
        //     http-5xx  >  network  >  http-4xx  >  unknown  >  no-failure
        //
        // `http-4xx` ranks BELOW `network` deliberately: a 4xx means the pipeline WORKED and refused
        // the payload, so it is less diagnostic of a broken collector even though it is louder.
        // The token is a SUMMARY — the counts ride unmixed beside it.
        //
        // ⛔ `unobservable` is deliberately NOT a token here, and it is not an omission: it is
        // the flush-time RESIDUAL (`sent - accepted - rejected`), so it rides the payload as a
        // COUNT and by construction cannot have an event — an event exists only where a callback
        // ran, and `unobservable` is precisely the no-callback case. Emitting it *and* computing
        // it would put two different quantities under one name (architect, 2026-09-18).
        //
        // ⚠️ `all-accepted` is an AMENDMENT to the frozen four, raised 2026-09-18T00:37Z to `api`
        // while `gateway-service!389` was still open — the only cheap moment, since after it merges
        // this is a second gateway MR for one word. web-first is still wrong: this list must remain a
        // SUBSET of the server's admitted set or gateway's pipeline reds.
        umami_send_window: ['http-4xx', 'http-5xx', 'network', 'unknown', 'no-failure'],
    },
    // Tokens the producer can actually emit. Since web!515 round 4 the
    // attribution is DEFINITION metadata — each route declares
    // `handle.telemetryRoute` and the producer resolves the deepest matched
    // handle (never the pathname, which is user-controlled text). This list is
    // exactly the declared range (`RENDER_FAILURE_EMITTED_ROUTES`, the only
    // range the emitter's bounded emission passes through). A screen claims its
    // token on its own route definition — chat-side in `routes.tsx`,
    // `agent-detail` on web-ui's section route, `bridge` on desktop's sibling —
    // and a route without a handle omits the context. Superset bound is
    // gateway's `clientTelemetryContextValueAllowlist["route"]`.
    context: {
        route: [
            'chat',
            'agents',
            'agent-create',
            'agent-detail',
            'settings',
            'account',
            'bridge',
        ],
        // Q4 (architect ruling, decisions/architect/2026-09-11-client-telemetry-admits-ability-row-
        // disagreement.md, + the follow-up enforcement-level ruling in the parent backlog card):
        // values are CLOSED to the ability catalog's ids, but — unlike `route` — that closure is
        // enforced server-side (gateway, catalog-derived; gw!358) and by ordinary TypeScript typing
        // here, NOT by a generator-side named-constant collector. `route` needs
        // `collectRouteInitializers` because a wrong-but-admissible value can be hand-authored (two
        // contract-valid route strings, so a stale literal passes every check silently);
        // `ability_id` has no such hazard because every emitted value is `row.abilityId` read
        // verbatim off a served AbilityRow — a wrong value there is an ordinary loop bug, not a
        // token-authoring hazard, and an AST check on the initializer would not catch it either. The
        // generator instead enforces one narrower predicate, in
        // `refuseLiteralAbilityIdInitializers` (scripts/gen-client-telemetry.mjs): a string-LITERAL
        // `ability_id` initializer inside a report() call is refused — enough to convert "never
        // hand-typed" from an asserted premise into an enforced one, without recreating `route`'s
        // machinery for a key that doesn't need it. The empty list below is the SAME convention
        // `umami_event_data` already uses for a declared-but-not-locally-enumerated key: `[]` means
        // "declared, no closed domain here" and stays distinguishable from the key being absent
        // (inadmissible). The catalog, not this file, is the source of truth for the id space — see
        // `abilityEntries()` from `@agnivo/proto/gen/ts/agnivo/abilitycatalog/abilityCatalog.gen`.
        ability_id: [],
    },
    // The wire VOCABULARY for `surface` — every value the producer may put on the
    // wire, which is not the same as the set it emits today. `WireSurface` in
    // `clientEvent.ts` is DERIVED from this list rather than restated beside it:
    // this file's header says a second hand-maintained list recreates the very
    // drift the contract exists to prevent, and a surface vocabulary split across
    // two declarations is that defect with a new name.
    //
    // ⛔ `mobile` is deliberately present and deliberately UNEMITTED — see the
    // `WireSurface` docblock for the full reasoning. Short version:
    // `apps/mobile/src/config.ts` sets `surface: 'web'`, so mobile traffic arrives
    // labelled `web`; the token exists to keep this vocabulary aligned with
    // `agnivo/proto`'s `ORIGIN_SURFACE_MOBILE`, which ships inert for the same
    // reason. Do not read a `mobile` column as mobile traffic, and do not "tidy"
    // it away as unused — dropping it makes the two vocabularies disagree where
    // they currently agree.
    //
    // "Aligned with proto" is a DOCUMENTED SUBSET, and as of 2026-09-03 it is also
    // CHECKED. Measured at proto `origin/main` `285de10`,
    // `proto/agnivo/agent/v1/agent_core.proto:289-296`, the enum has SIX members
    // against this vocabulary's four:
    //
    //   UNSPECIFIED(0) TELEGRAM_BOT(1) MINIAPP(2) WEB_UI(3) DESKTOP(4) MOBILE(5)
    //
    // Both omissions are correct and intended — `UNSPECIFIED` is a fail-closed zero
    // value, and `TELEGRAM_BOT` is a server-side ORIGIN, not a client shell that
    // could ever run this producer. So the relation is a deliberate subset, not a
    // mismatch. (architect, review of web!531, 2026-09-02.)
    //
    // ⚠️ **What this paragraph said until 2026-09-03, and why it no longer does.**
    // It read *"Nothing verifies it … Treat the two vocabularies as related by prose
    // and human review; do not cite this comment as evidence that anything enforces
    // the relation."* That was accurate when written and is the gap it correctly
    // named: a future `ORIGIN_SURFACE_*` addition diverged SILENTLY, in exactly the
    // way this file's header describes and this contract otherwise prevents.
    //
    // The enforcement now exists — `test/clientEvent.test.ts`, describe *"the wire
    // vocabulary agrees with proto OriginSurface"*. It imports the enum from the
    // pinned `@agnivo/proto` (a devDependency, so test-only — see the `verdict.ts`
    // note on why a value import in `src/` would be wrong) and requires every member
    // to be EITHER mapped to a token here OR listed as a documented non-shell with
    // its reason. A seventh member is in neither and reds. ⓘ A member REMOVED
    // upstream reds at `typecheck` first (`TS2339` on the enum reference), which is
    // the louder signal — at runtime it merely reads back `undefined`.
    // ⛔ It is a subset check in ONE direction by design: do not "tidy" it into a
    // set-equality assertion, which would demand `TELEGRAM_BOT` be a client shell.
    //
    // Consumed by gateway's `ClientTelemetryAdmittedSurfaces()` via
    // `contract/client-telemetry.json` (gateway-service!313). Until this key
    // reaches the artifact, that check is vacuous BY CONSTRUCTION — its declared
    // count going 0 -> 4 is the signal the guard closed end to end.
    wire_surfaces: ['web', 'desktop', 'miniapp', 'mobile'],
    // ADR-141 D1.2 — the ANALYTICS shell identity. Eight values, one per deployable
    // shell, each set EXPLICITLY by that shell; never derived from `Surface`, from
    // a viewport, or from a user agent.
    //
    // ⛔ A DIFFERENT AXIS FROM `wire_surfaces` — do not fold them together, and do
    // not "reconcile" the two lists. They are similar in shape and that is exactly
    // the trap (architect, ADR-141 D1.2 ruling 2026-09-02):
    //   - `wire_surfaces` (4) is a wire/transport classification the GATEWAY
    //     asserts against — `ClientTelemetryAdmittedSurfaces()` bounds it.
    //   - `client_shells` (8) is shell IDENTITY for analytics only, never read by
    //     runtime logic, and nothing on the server admits against it.
    // The two things analytics must separate — `landing` vs `web-ui`, and
    // `admin-platform` vs `admin-tenant` — are distinctions neither the runtime
    // `Surface` union nor the wire vocabulary has any reason to make.
    //
    // ⚠️ SPELLING IS THE FRAGILE PART, not the membership. `web-ui` (not `web`),
    // `mini-app`, `admin-platform`, `admin-tenant` — hyphens throughout. A producer
    // that disagrees on spelling does NOT error: it silently splits one shell across
    // two dashboard series, which reads as two smaller shells rather than as a bug.
    //
    // ⓘ 6 of these 8 shells compose `@agnivo/app-core` and are constrained at
    // COMPILE TIME by `ClientShell` below. `landing` and `login-ui` do not compose
    // app-core (Next.js apps; `login-ui` is a vendored zitadel subtree, the worst
    // place in this repo to add an import edge, since subtree re-imports arrive
    // wholesale and would drop it silently). Those two are constrained by
    // `scripts/client-shell-declarations.test.mjs` asserting their literal against
    // the GENERATED artifact. **A stated 6/2 split, not a gap** — and the same
    // not-an-app-core-shell property is also why those two carry a standalone
    // `connect-src 'self'` CSP.
    client_shells: [
        'landing',
        'web-ui',
        'admin-platform',
        'desktop',
        'mini-app',
        'mobile',
        'login-ui',
    ],
    // ADR-141 D1.1 — declared-but-unarmed shells. A shell in this list BOTH declares
    // route tokens (via `handle.telemetryRoute`) AND lacks `config.analytics` (so the
    // route-view producer is inert). The dashboard reader's question — "for shell X,
    // route Y, why are there no Umami rows?" — has two answers ("unarmed" vs.
    // "gone-quiet") that demand OPPOSITE responses; this list records the FIRST
    // answer for the shells where it applies, so a reader of the contract alone can
    // distinguish them without remembering the per-shell config.
    //
    // Architect ruling 2026-09-04 (decisions/architect/2026-09-04-adr141-desktop-
    // route-views-stay-unarmed-and-the-token-is-declared-unarmed.md): desktop's
    // `bridge` token is the documented case — the binary emits no telemetry by
    // design (a different privacy posture from a web page doing it), and the token
    // is kept so the producer, IF armed later, has its name ready. Recording the
    // unarmed state here closes the dashboard's ambiguity.
    //
    // ⛔ DO NOT add a shell here just because it lacks analytics. Only add a shell
    // here if it BOTH declares route tokens AND lacks analytics. A shell with no
    // route tokens is "no producer expected" by design — its absence here is
    // implicit, and listing it would imply an armed-but-silent producer (also bad,
    // also worth distinguishing — but a different entry if it ever arises).
    //
    // ⛔ DO NOT fold this into `client_shells` — that list is a VOCABULARY
    // (every shell in the repo). This list is a STATE subset (only those that are
    // declared-but-unarmed). The two change at different cadences: a new shell
    // adds to `client_shells`; an arming or disarming decision adds here.
    //
    // ⓘ Today: `desktop` only (via the `bridge` sibling in apps/desktop/src/App.tsx).
    // 5 of 8 shells (admin-platform, admin-tenant, mini-app, mobile, landing,
    // login-ui) declare no route tokens — implicit unarmed, not listed.
    declared_unarmed_shells: [
        'desktop',
    ],
    // ADR-141 D1.1 — the Umami-plane `event_data` ALLOWLIST, per event. Anything not
    // named here is dropped BEFORE send, by the one helper allowed to send.
    //
    // ⛔ AN ALLOWLIST, NEVER A BLOCKLIST, and the asymmetry is the whole point. A
    // blocklist ("strip `query`, `message`, …") fails OPEN on the key nobody thought
    // to list — and the keys nobody thinks to list are exactly the ones that leak,
    // because they arrive later than the rule. An allowlist fails CLOSED: a new key
    // is dropped until someone adds it here, which is a visible, reviewable act.
    // Same reasoning the fleet's redaction rule uses when it demands provable
    // completeness instead of an enumeration of things to hide.
    //
    // ⛔ A THIRD PLANE, and it is NOT the gateway's. `event_types` above crosses the
    // custom app-core→gateway client-events path (bounded technical errors); this
    // crosses to Umami and the gateway never sees it. Do not admit a key here on the
    // grounds that gateway would drop it — gateway is not in this path, so there is
    // no server-side backstop and this list IS the enforcement.
    //
    // ⚠️ `correlation_token` is the ONLY correlation carrier, and `event_data` is the
    // only place it may ride (ADR-141 D2): never the URL, query, referrer, log,
    // localStorage, cookie, IndexedDB, `distinct_id`, or any session-scoped column.
    // It is minted per action by `mintCorrelationToken()` and is single-use — one
    // Umami row and one Class-1 row per token. Adding a second identifier-shaped key
    // here would give a second join path and break that 1+1 cardinality, so a new key
    // needs an argument that it cannot correlate two rows.
    //
    // ⓘ NOT a visitor id. ADR-141 rev 2.1 carries no visitor-scoped identifier at
    // all; `anon_visitor_id` was deleted from the spec, not renamed or deferred. Do
    // not reintroduce one here and do not treat the action token as a substitute —
    // a lifetime-bounded per-visitor value is a broader capability the ADR withholds.
    // ⛔ `funnel_step` carries NO `correlation_token`, and that is condition 4 of the
    // architect's 2026-09-02 ruling, not an oversight: no D2 action token, no journey
    // key, no cross-plane join, no per-user attribution. These are single-plane counts.
    // Adding the token here would give the funnel a join path into the Class-1 plane
    // and put it inside D5.2, which is exactly what scoping it this way avoids.
    //
    // ⭐ The allowlist is PER EVENT NAME, so `route_view`'s list is untouched and this
    // event's dimensions cannot leak into it. That isolation is stronger than riding a
    // shared event would have given, and it is why one new name beat adding a dimension
    // to `route_view` on the merits, not merely on availability.
    //
    // ⛔ SHAPE: `event -> key -> VALUE DOMAIN`, not `event -> key list` (architect
    // option 2, 2026-09-02, overruling product's option 1). D1.1's word "bounded" is a
    // claim about VALUES, and until this shape landed there was nowhere in the artifact
    // to state one: `context.route` and `event_types.*` declare value vocabularies,
    // `umami_event_data.<event>` declared key NAMES only. The two existing keys hid the
    // gap — `client_shell` borrows the separate `client_shells` list, `correlation_token`
    // is minted with an empty parameter list — and `funnel_step` was the first key whose
    // admissible values had nowhere to live.
    //
    // ⛔ `[]` MEANS "DECLARED, NO CLOSED VALUE DOMAIN HERE" — it does NOT mean "no
    // values admitted", and it is NOT the same as the key being ABSENT (architect's
    // condition 2). Absent = the key is inadmissible and gets dropped. `[]` = the key
    // is admissible and this contract does not constrain its values. Both states must
    // stay representable, which is why the generator refuses an empty EVENT (vacuous:
    // a rule that drops everything, silently) but permits an empty VALUE LIST.
    //
    // ⭐ `[]` NO LONGER CONFLATES ANYTHING, and the way that was fixed is the point.
    // It used to mean both "genuinely unbounded" (`correlation_token`, an opaque
    // per-action mint) and "bounded, but the vocabulary lives elsewhere"
    // (`client_shell`, bounded by `client_shells` one field up). Two situations, one
    // encoding, because the dialect had no way to say the second.
    //
    // ⇒ It does now: a value domain may NAME a vocabulary — `client_shell:
    // ['@client_shells']` (architect's ruling, 2026-09-02). So the ambiguous case is
    // gone rather than documented harder, and `[]` has exactly one meaning left:
    // **genuinely unbounded**. `correlation_token` is the only key that carries it.
    //
    // ⛔ SO THE RULE FOR A NEW KEY IS NOW SHARP, where it used to require judgement:
    // if its values are bounded by a vocabulary in this contract, REFERENCE that
    // vocabulary. If bounded by a list that is not here, add the list here first and
    // then reference it — do NOT paste a copy in as a literal domain, and do NOT fall
    // back to `[]`, which would now be a false claim that the key is unbounded.
    //
    // ⚠️ The sigil rides inside the array grammar (`['@name']`, not `'@name'`) so the
    // generator's literal parser is untouched and still evaluates nothing; resolution
    // is a separate pass on both sides. `@` cannot begin a real token, so a reference
    // that lands anywhere references are not allowed is refused as an invalid token.
    umami_event_data: {
        // ⛔ Event name is `activation_funnel`, dimension is `funnel_step`. The event was
        // briefly `funnel_step` too (web!544/!545), which made the artifact read
        // `funnel_step.funnel_step` and invited exactly the four-names-instead-of-four-
        // tokens misreading D1.1 gates.
        //
        // FREE TO RENAME, and the licence rests on THIS EVENT HAVING NO PRODUCER — not on
        // the collector being unconfigured. Re-derive rather than trust this sentence:
        //
        //   git grep -n activation_funnel -- packages/ apps/ \
        //     | grep -v Contract | grep -v '\.test\.'
        //
        // Empty ⇒ nothing emits it, so it has never fired and no dashboard or saved report
        // can name it. Measured empty 2026-09-04.
        //
        // ⭐ Run the CONTROL in the same breath, or an empty result is unreadable: substitute
        // `route_view` for `activation_funnel` above and the same search returns 3 lines
        // across 2 files (`routeViewSender.ts`, `AgentCreateScreen.tsx`). A zero is a claim
        // about the search as much as about the code, and only a non-zero on a known-present
        // term tells you the search reaches.
        //
        // ⛔ This used to be grounded on "the collector is unconfigured on every shell …
        // nothing in CI sets them", and that HALF IS NOW FALSE: `.gitlab-ci.yml`'s
        // `DOCKER_BUILD_PUBLIC_ARGS` passes a real `VITE_UMAMI_URL` and website id on
        // web-ui's build lane, so web-ui ships instrumented. The other five app-core shells
        // remain unconfigured, which is why the conclusion survived while its premise did
        // not — the dangerous shape, because the sentence still read as a clearance.
        // Producer-absence is the better ground for the same licence: it is a property of
        // this repo that a grep settles, rather than a property of deploy config that drifts
        // per shell and is edited in a different file by a different concern.
        activation_funnel: {
            client_shell: ['@client_shells'],
            funnel_step: [
                'first_agent_open',
                'agent_create_confirmed',
                'first_message_sent',
                'first_answer_visible',
            ],
        },
        route_view: {
            // ADR-141 2026-09-13 amendment — a bounded DEPLOYMENT dimension, not a
            // per-actor one: `auth_mode` is read from `VITE_AUTH_MODE` at BUILD time,
            // so every user of a given deployment emits the same value. Vocabulary is
            // the UNION across apps (no single app can emit all four) — a per-app
            // vocabulary would make the column un-aggregatable across shells, which is
            // the only reason to have it on a shared plane. `mock` stays IN the closed
            // set on purpose: it is what makes a misconfigured production deploy
            // visible AS A VALUE instead of silently dropped as an unknown field.
            auth_mode: ['mock', 'oidc', 'bridge', 'telegram'],
            client_shell: ['@client_shells'],
            // ⚠️ DISABLE FLAGGED FOR REVIEW, not applied quietly — the D9.12 rule's own
            // docblock says "expect to add `eslint-disable-next-line` NEVER; expect to route
            // the value through the helper instead", and overriding that silently is not a
            // call I get to make alone. Why it is nonetheless correct HERE: the rule's
            // `Property` visitor fires on any property named `correlation_token`, and option
            // 2 turned this name from a STRING INSIDE A LIST into a KEY. This is not a token
            // write. It is a declaration that the key is admissible with no closed value
            // domain, its value is `[]`, and no token can be a `[]` — the minted token still
            // comes from `mintCorrelationToken()` in `routeViewSender`, where the rule
            // rightly watches it. The rule is not weakened, `tokenNames` is not narrowed,
            // and the disable is one line.
            // ⇒ Raised with architect + reviewer: the precise fix is for the rule to skip an
            // `ArrayExpression` value (structurally never a token), which is a rule change
            // with its own mutation tests and does not belong in a contract-shape MR.
            // eslint-disable-next-line agnivo-analytics/no-unminted-correlation-token -- contract VALUE-DOMAIN declaration, not a token write; see the note above
            correlation_token: [],
        },
        // ADR-082 mini-app tg_uid-missing reopen surface (architect ruling
        // `decisions/architect/2026-09-14-miniapp-tguid-counters-one-event-with-a-step-dimension-no-token-self-serve.md`,
        // R-A): ONE event carrying a `step` dimension, not two event names — the same
        // shape `activation_funnel` already uses, for the same reason (R-A quotes R2's
        // 2026-09-02 condition 3 verbatim: "what the allowlist forbids is the vocabulary
        // escaping into the event-NAME space"). Product asked for two counters
        // (`error_received`, `reopen_clicked`); this is that ask expressed as one event
        // with a two-token dimension so `copy_version` lives in exactly one allowlist and
        // the click/error ratio is a group-by, not a join.
        //
        // ⛔ NO `correlation_token` (R-B): the token buys a join OUT of the Umami plane
        // into the Class-1 plane (D2), and everything this event answers — counts by
        // `copy_version`, the click/error ratio — lives WITHIN the Umami plane. Mint the
        // token when a question needs the cross-plane join, not when a sender merely can.
        //
        // `error_info_reason` carries `google.rpc.ErrorInfo`'s `reason`
        // (`MINIAPP_TGUID_MISSING`, `telegramInitDataAuth.ts`'s `TGUID_MISSING_REASON`).
        // `ErrorInfo.domain` is deliberately NOT sent: it was declared unbounded (`[]`) and
        // forwarded verbatim, which ADR-141 D1.1 forbids (free-form event_data), and
        // `reason` already identifies the signal (architect, 2026-10-11).
        //
        // `error_info_reason: ['MINIAPP_TGUID_MISSING']` (closed): unlike `domain`,
        // `reason` IS a token (`TOKEN_PATTERN` matches it — measured, not assumed) and this
        // ADR-082 classifier only ever emits this one value, so it gets a real closed
        // domain enforced by `allowlistedData`, not an unbounded `[]` resting on an unreal
        // producer bound (the sender's input type is plain `errorInfoReason: string`, so
        // nothing at the types stopped a caller-supplied value from reaching an unbounded
        // field). `TGUID_MISSING_DOMAIN`/`TGUID_MISSING_REASON` live in
        // `apps/mini-app/src/auth/telegramInitDataAuth.ts:56-57` — a different package
        // from `packages/app-core`, which is why the sender's own type is `string` rather
        // than importing those constants; that is a fine reason for the type shape and a
        // different one from why the contract closes the domain.
        // `tg_uid_present` is a string-boolean (`'true'`/`'false'`) rather than a real
        // boolean because every Umami `data` value is a string; declared BOTH ways even
        // though this producer only ever sends `'false'` (the event exists because tg_uid
        // is missing) — a closed domain of one real value would silently overclaim that no
        // producer could ever emit the other.
        miniapp_tguid_missing: {
            client_shell: ['@client_shells'],
            step: ['error_received', 'reopen_clicked'],
            copy_version: ['v1'],
            error_info_reason: ['MINIAPP_TGUID_MISSING'],
            tg_uid_present: ['true', 'false'],
        },
        // ADR-051 S7c-7 — the entitlement/trust-clamp surface's analytics (architect ruling
        // `decisions/architect/2026-09-18-s7c7-entitlement-surface-analytics-event-shape.md`).
        //
        // ⛔ ITS OWN EVENT, NOT A FOLD INTO `route_view`. `route_view` counts navigation;
        // this counts a component rendering a standing clamp — different questions, same
        // reason `activation_funnel` got its own name rather than overloading `route_view`.
        //
        // `state` is CLOSED to exactly the two states `classifyTrustSurface` can produce
        // today (`packages/app-core/src/entitlement/trustSurface.ts`) — `trust_limited`
        // (SHADOW_LIMITED/COLD_START) is a real, reachable classifier output but has no
        // copy on this surface (`copy.ts`'s `trust_limited: null`) and so never renders,
        // never reports. Widening this list without a producer is the phantom-producer
        // class the ruling calls out for `step` below.
        //
        // `step` is `['shown']` ONLY, deliberately not `['shown', 'dismissed']` even
        // though product's original spec (`decisions/product/2026-08-30-adr051-reason-
        // tagged-upsell-surface.md`, "Analytics/self-learning") names "dismissed":
        // `AccessSurface.tsx` (S7c-2) has no dismiss affordance today, so `dismissed`
        // would be a declared value with no producer. Add it in the same MR that adds the
        // affordance — `step` is the dimension this event is built to grow through, not a
        // fixed pair.
        //
        // ⛔ NO `correlation_token` (ADR-141 D2): mint one when a question needs the
        // cross-plane join, not when a sender merely can. "How many sessions see a trust
        // clamp, and in which shell" is a Umami-plane count; nothing on the Class-1 plane
        // joins to it.
        //
        // NOTHING BEYOND THESE THREE KEYS — no reason text, plan/tier, tenant or user id.
        // Plan/tier stays out until it is on the read-path payload itself
        // (`GetMyUsageResponse.entitlement` has no tier field); `TenantUsage.planId` is an
        // adjacent field, and deriving from it here is the same anti-pattern `verdict.ts`
        // forbids for render, forbidden here for the same reason. `resolved_verdict` and
        // `cta_selected` wait for S7c-6's action-refusal flow, itself blocked on the
        // ErrorInfo producer gap (`decisions/architect/2026-09-01-adr051-s7c-errorinfo-
        // arm-has-no-producer-correction.md`) — not this event's to anticipate.
        entitlement_surface: {
            client_shell: ['@client_shells'],
            state: ['trust_frozen', 'trust_banned'],
            step: ['shown'],
        },
    },
};
/**
 * ADR-141 D1.1 — the closed `funnel_step` value vocabulary (architect ruling
 * 2026-09-02, condition 1).
 *
 * ⛔ FOUR TOKENS ON ONE EVENT NAME, never four event names. That is the whole
 * distinction D1.1 gates: event names are unbounded and carry no property schema at
 * the vendor, so promoting these to names is a new event FAMILY and needs Product
 * plus an ADR amendment. One name carrying a bounded property is the operation the
 * ADR already provides for. If this ever drifts toward four names, it has left the
 * ruling.
 *
 * ⛔ DERIVED FROM THE CONTRACT LITERAL, never restated beside it — architect's
 * condition 1: "the const and the JSON list must become ONE SOURCE, not two lists
 * that agree today." This const previously held its own copy of the four tokens
 * because the artifact had nowhere to publish a value domain. It now does
 * (`umami_event_data.activation_funnel.funnel_step`), so the copy is deleted rather
 * than kept-and-cross-checked.
 *
 * ⚠️ A cross-check test between two hand-maintained lists would have been the
 * TEMPTING fix and is a weaker one: it proves they agree at the moment it runs, and
 * it is exactly the "second hand-maintained list" this file's own header names as the
 * drift the contract exists to prevent. Derivation makes disagreement unrepresentable
 * instead of detectable.
 *
 * ⓘ The reference is deep (`.umami_event_data.activation_funnel.funnel_step`) and that
 * is load-bearing, not incidental: renaming the event or the dimension in the literal
 * breaks THIS line at compile time, which is the whole point of the single source.
 */
export const FUNNEL_STEP_TOKENS = CLIENT_TELEMETRY_CONTRACT.umami_event_data.activation_funnel.funnel_step;
const FUNNEL_STEP_TOKEN_SET = new Set(FUNNEL_STEP_TOKENS);
/**
 * Runtime half of the closed vocabulary. The compile-time union covers every call
 * site inside this monorepo; this covers the rest — an untyped JS caller, a value
 * widened to `string` on the way through a context boundary, or a token that survived
 * a rename in one place and not another.
 */
export function isFunnelStepToken(value) {
    return FUNNEL_STEP_TOKEN_SET.has(value);
}
/**
 * ADR-082 mini-app tg_uid-missing `step` vocabulary — same DERIVED-not-restated
 * discipline as `FUNNEL_STEP_TOKENS` above, and the same reason: this file's header
 * bans a second hand-maintained list.
 */
export const TGUID_MISSING_STEP_TOKENS = CLIENT_TELEMETRY_CONTRACT.umami_event_data.miniapp_tguid_missing.step;
const TGUID_MISSING_STEP_TOKEN_SET = new Set(TGUID_MISSING_STEP_TOKENS);
/** Runtime half of the closed vocabulary, same reasoning as `isFunnelStepToken`. */
export function isTguidMissingStep(value) {
    return TGUID_MISSING_STEP_TOKEN_SET.has(value);
}
/**
 * ADR-051 S7c-7 `state` vocabulary — same DERIVED-not-restated discipline as
 * `FUNNEL_STEP_TOKENS`/`TGUID_MISSING_STEP_TOKENS` above.
 */
export const ENTITLEMENT_SURFACE_STATE_TOKENS = CLIENT_TELEMETRY_CONTRACT.umami_event_data.entitlement_surface.state;
const ENTITLEMENT_SURFACE_STATE_TOKEN_SET = new Set(ENTITLEMENT_SURFACE_STATE_TOKENS);
/** Runtime half of the closed vocabulary, same reasoning as `isFunnelStepToken`. */
export function isEntitlementSurfaceState(value) {
    return ENTITLEMENT_SURFACE_STATE_TOKEN_SET.has(value);
}
const CLIENT_SHELL_SET = new Set(CLIENT_TELEMETRY_CONTRACT.client_shells);
/**
 * Runtime membership test.
 *
 * ⚠️ NOT a substitute for the compile-time union — it cannot be, since a Next.js
 * app that does not compose app-core cannot call it either. It exists so any
 * consumer has one definition point rather than re-deriving membership.
 */
export function isClientShell(value) {
    return CLIENT_SHELL_SET.has(value);
}
const CLIENT_EVENT_ROUTE_SET = new Set(CLIENT_TELEMETRY_CONTRACT.context.route);
const DECLARED_UNARMED_SHELL_SET = new Set(CLIENT_TELEMETRY_CONTRACT.declared_unarmed_shells);
/**
 * Runtime membership test for declared-but-unarmed shells.
 *
 * Same `failure direction` as `isClientShell`: a shell not in the set is
 * IMPLICITLY armed-or-no-routes, and `false` is the safe return for an unknown
 * value. A misspelled shell name returns `false`, not a runtime error.
 */
export function isDeclaredUnarmedShell(value) {
    return DECLARED_UNARMED_SHELL_SET.has(value);
}
/** Runtime half of the route contract: undeclared route tokens never cross the wire. */
export function isClientEventRoute(value) {
    return CLIENT_EVENT_ROUTE_SET.has(value);
}
// Emitters use named runtime values rather than repeating wire strings. The generator also scans
// eventType object properties and refuses unknown or literal values, so adding a producer without
// extending the contract cannot publish a shorter send-set.
export const VOICE_PLAYBACK_FAILED_EVENT_TYPE = 'voice_playback_failed';
export const RENDER_FAILURE_EVENT_TYPE = 'render_failure';
export const UNHANDLED_EXCEPTION_EVENT_TYPE = 'unhandled_exception';
export const ABILITY_ROW_DISAGREEMENT_EVENT_TYPE = 'ability_row_disagreement';
/** The windowed Umami send accounting — see the `umami_send_window` entry above. */
export const UMAMI_SEND_WINDOW_EVENT_TYPE = 'umami_send_window';
export const UMAMI_SEND_WINDOW_HTTP_4XX = 'http-4xx';
export const UMAMI_SEND_WINDOW_HTTP_5XX = 'http-5xx';
export const UMAMI_SEND_WINDOW_NETWORK = 'network';
export const UMAMI_SEND_WINDOW_UNKNOWN = 'unknown';
/**
 * The `errorName` for a window that observed NO CLASSIFIED FAILURE — the LIVENESS case.
 *
 * ⛔ IT DOES NOT MEAN "ALL ACCEPTED", and the difference is load-bearing rather than pedantic:
 * `unobservable` is a residual, so `rejected == 0` is compatible with `accepted < sent`. The
 * spelling that claimed acceptance was refused for exactly this reason — see the contract entry.
 */
export const UMAMI_SEND_WINDOW_NO_FAILURE = 'no-failure';
export const ENABLED_WHILE_LOCKED_ERROR_NAME = 'enabled_while_locked';
export const COUPLED_ENABLED_MISMATCH_ERROR_NAME = 'coupled_enabled_mismatch';
export const ROUTE_VIEW_EVENT_NAME = 'route_view';
/** ADR-141 D1.1 — the funnel carrier's event name, derived from the same literal. */
export const FUNNEL_STEP_EVENT_NAME = 'activation_funnel';
/** ADR-082 — the mini-app tg_uid-missing reopen surface's Umami event name. */
export const MINIAPP_TGUID_MISSING_EVENT_NAME = 'miniapp_tguid_missing';
/** ADR-051 S7c-7 — the entitlement/trust-clamp surface's Umami event name. */
export const ENTITLEMENT_SURFACE_EVENT_NAME = 'entitlement_surface';
const EMPTY_KEY_SET = new Set();
const UMAMI_EVENT_DATA_SETS = new Map(Object.entries(CLIENT_TELEMETRY_CONTRACT.umami_event_data).map(([event, keyMap]) => [
    event,
    new Set(Object.keys(keyMap)),
]));
/**
 * The vocabularies a value domain may name, and the RUNTIME HALF of a rule that is
 * implemented twice.
 *
 * ⛔ ITS TWIN IS `resolveValueDomain` IN `scripts/gen-client-telemetry.mjs`. They
 * cannot share a module — that is a build-time `.mjs` script, this is app-core TS —
 * so one rule has two implementations, and if they disagree the published artifact
 * and this runtime admit DIFFERENT SETS. That is the same producer/artifact drift
 * this contract exists to prevent, arriving from the inside.
 *
 * ⇒ `packages/app-core/test/umamiEventDataEquivalence.test.ts` is what pins them: for every
 * event and key in the published artifact, `admitsUmamiEventData` must accept exactly
 * the declared values and nothing else. Architect made it a condition of the
 * reference form, not a follow-up. If you change either resolver, that test is the
 * one that has to stay green — and if you are deleting it, you are re-opening the
 * defect the reference form was approved on the strength of closing.
 *
 * ⚠️ Keep this set in step with the generator's `resolvableVocabularies`. It is an
 * explicit allowlist on both sides for the same reason: a derived set would make
 * every future top-level key referencable, including the ones that are not token
 * lists.
 */
const REFERENCE_SIGIL = '@';
const RESOLVABLE_VOCABULARIES = {
    client_shells: CLIENT_TELEMETRY_CONTRACT.client_shells,
    wire_surfaces: CLIENT_TELEMETRY_CONTRACT.wire_surfaces,
};
/** `null` ⇒ the reference did not resolve. NOT the same as `[]`; see the builder. */
function resolveValueDomain(values, vocabularies) {
    // A reference is the whole list or nothing — the generator refuses a mixed list, so
    // this side never has to define what a mix would mean. Reading only element 0 of a
    // single-element array keeps the two implementations answering the same question.
    const [only] = values;
    if (values.length !== 1 || only === undefined || !only.startsWith(REFERENCE_SIGIL))
        return values;
    const name = only.slice(REFERENCE_SIGIL.length);
    // ⛔ `null`, NOT `[]`, and the difference is the whole safety property. Returning `[]`
    // here would be indistinguishable from a contract-declared empty domain, which means
    // "no constraint" — so the builder's `length > 0` filter would drop the key and
    // `admitsUmamiEventData` would then admit EVERY value for it. A typo'd reference would
    // silently become the most permissive state in the system. `null` keeps the key in the
    // map with an empty Set, which admits nothing.
    // ⓘ Unreachable through a published contract — the generator refuses an unresolvable
    // name outright. This arm exists for a hand-edited or half-rebased literal, where the
    // failure has to be a dropped dimension rather than an open one.
    return vocabularies[name] ?? null;
}
/**
 * Declared VALUE domains, per event, per key. A key with `[]` in the contract is
 * ABSENT from its event's inner map — "declared, no closed domain" — which is what
 * makes `has(key)` on the outer set and `get(key)` on this inner map answer two
 * different questions.
 */
/**
 * ⛔ EXPORTED, AND ONLY BECAUSE THE FAIL-CLOSED ARM WAS OTHERWISE UNTESTABLE.
 *
 * `resolveValueDomain`'s `?? null` branch — the one that stops an unresolvable reference
 * from becoming the most permissive state in the system — is unreachable through the real
 * contract, because the generator refuses an unresolvable name and the literal is
 * `as const`. That is good for safety and fatal for coverage: measured 2026-09-03, mutating
 * `?? null` to `?? []` left the ENTIRE app-core suite green. The guard existed and nothing
 * could have told us if it stopped working.
 *
 * ⇒ Parameterising the builder makes every state reachable from a test with a synthetic
 * contract, WITHOUT widening what production code can do — the module still calls it with
 * exactly one contract and one vocabulary table.
 *
 * ⚠️ Do not use this to build a second live map. Its only production call is the one below.
 */
export function buildUmamiEventDataValues(umamiEventData, vocabularies) {
    return new Map(Object.entries(umamiEventData).map(([event, keyMap]) => [
        event,
        new Map(Object.entries(keyMap)
            .map(([key, values]) => [key, resolveValueDomain(values, vocabularies)])
            // ⛔ THREE OUTCOMES, TWO OF WHICH LOOK ALIKE AND MEAN OPPOSITE THINGS:
            //   resolved.length > 0 ⇒ a real domain; only these values admitted.
            //   resolved === []     ⇒ contract-declared empty = NO constraint; drop the
            //                         key from this map so `admitsUmamiEventData` sees
            //                         `undefined` and admits anything (`correlation_token`).
            //   resolved === null   ⇒ an UNRESOLVABLE reference; keep the key with an
            //                         empty Set so it admits NOTHING.
            // ⓘ This predicate reads the RESOLVED value, so it has to sit after the
            // resolve step — a coupling, NOT a hazard, and the distinction matters to
            // whoever refactors this.
            // ⛔ An earlier version of this comment claimed "filtering first would drop
            // all eight real values". That was false and is corrected rather than deleted:
            // `['@client_shells']` has length 1, so it passes a raw `length > 0` filter
            // and resolves afterwards to the same eight. Architect questioned it on
            // review; measured by applying the pure reorder, and every test stayed green.
            // ⛔ ...AND THE REPLACEMENT SENTENCE WAS ALSO WRONG. It read: "what is
            // load-bearing is that whatever filter you write classifies the TRI-STATE".
            // Architect refuted it with a measurement (review of !547) and it is corrected
            // here rather than quietly reworded, because it is the second wrong claim in
            // the same comment: the REORDERED filter reads the RAW values, which are never
            // `null`, so it classifies nothing at all — its correctness comes from the
            // `?? []` downstream instead.
            //
            // ⭐ THE ACCURATE INVARIANT, one notch further out: **`null` and `[]` must be
            // distinguished SOMEWHERE between resolve and the Set.** Where that happens is
            // free; that it happens is not.
            //
            // ⚠️ And a FOURTH state neither of us enumerated makes the placement observable:
            // a RESOLVABLE reference to an EMPTY vocabulary (`['@client_shells']` where
            // `client_shells` is `[]`). Reproduced both ways —
            //
            //   admitsUmamiEventData('route_view', 'client_shell', 'not_a_shell')
            //     resolve-then-filter (shipped): true   ⇒ admits EVERYTHING (fail-open)
            //     filter-then-resolve:           false  ⇒ admits NOTHING   (fail-closed)
            //
            // ⇒ The two orders are NOT equivalent in general; they coincide only while every
            // referencable vocabulary is non-empty. That holds on both sides today and is
            // enforced, not assumed: the generator runs `client_shells`/`wire_surfaces`
            // through `canonicalTokens` WITHOUT `allowEmpty`, so an empty vocabulary is
            // unpublishable, and the literal is `as const`. ⛔ If you ever add a referencable
            // vocabulary that may legitimately be empty, this comment is the one to re-read:
            // the shipped order takes the fail-OPEN branch for it.
            .filter(([, resolved]) => resolved === null || resolved.length > 0)
            .map(([key, resolved]) => [key, new Set(resolved ?? [])])),
    ]));
}
/**
 * Declared VALUE domains for the REAL contract. The only production call to the builder.
 */
const UMAMI_EVENT_DATA_VALUES = buildUmamiEventDataValues(CLIENT_TELEMETRY_CONTRACT.umami_event_data, RESOLVABLE_VOCABULARIES);
/**
 * Runtime half of the allowlist: the keys this event may carry, or an EMPTY set for
 * an event the contract does not declare.
 *
 * ⛔ The empty-set return is deliberate and is the fail-closed direction. An
 * undeclared event name yields a set that admits nothing, so its data is dropped
 * entirely rather than passed through unfiltered. A `undefined` return would have
 * invited `?? allowEverything` at the call site, which is the blocklist failure
 * mode wearing an allowlist's name.
 */
export function umamiEventDataKeys(eventName) {
    return UMAMI_EVENT_DATA_SETS.get(eventName) ?? EMPTY_KEY_SET;
}
/**
 * The full ADR-141 D1.1 admission test for one `event_data` pair: is this KEY declared
 * for this event, and — if the contract bounds the key's values — is this VALUE in the
 * declared domain?
 *
 * ⛔ ONE PREDICATE, NOT TWO COMPOSABLE HELPERS, and that is a safety property rather
 * than a style preference. The obvious alternative was to expose the value domain
 * alongside `umamiEventDataKeys` and let callers `&&` them. But a "domain" lookup for a
 * key the event does not declare has to answer something, and every available answer is
 * wrong: `null`/`undefined` reads as "unbounded, admit it" and an empty set reads as
 * "admit nothing" for keys that are legitimately unbounded. Correct use would depend on
 * checking membership FIRST — an ordering rule that lives in the caller's head, holds in
 * the one call site written today, and fails silently in the one written next year. Here
 * the two checks cannot be separated, so there is no wrong order to get.
 *
 * ⚠️ Undeclared event name ⇒ `false` for everything, via the empty key set. Fail-closed,
 * same direction as `umamiEventDataKeys`.
 */
export function admitsUmamiEventData(eventName, key, value) {
    if (!umamiEventDataKeys(eventName).has(key))
        return false;
    const domain = UMAMI_EVENT_DATA_VALUES.get(eventName)?.get(key);
    // No declared domain ⇒ the key is admissible and this contract does not constrain
    // its values (`correlation_token`, `client_shell`). See the `[]` note on the literal
    // for why that is two different situations wearing one encoding.
    return domain === undefined || domain.has(value);
}
//# sourceMappingURL=clientTelemetryContract.js.map